/**
 * Starts the third-party tools a visitor's consent allows, each once per page,
 * and gives Google Analytics and Clarity the visitor's consent signals. A tool
 * whose group is not allowed is never injected, so the browser makes no
 * request to its host. Events the app fires before a tool starts are dropped,
 * not queued: `trackEvent` skips while `window.gtag` is missing.
 *
 * Aliases: tracking scripts, analytics tags, Google Analytics, Meta Pixel,
 * Microsoft Clarity, SiteBehaviour, Ahrefs, Cloudflare Web Analytics,
 * affiliate script, vendor cookies
 */

import { startErrorReplay } from "~/lib/monitoring/error-replay";
import type { ConsentGroup, ConsentGroups } from "~/lib/shared/consent";

const GOOGLE_ANALYTICS_ID = "G-V1LBTZJYJG";
const CLARITY_PROJECT_ID = "petk9fvk1i";
const META_PIXEL_IDS = ["1555911545046104", "1044068191085252"];
const SITEBEHAVIOUR_SECRET = "2b79a9d3-94bc-4d56-a7a0-96e0e1f7a7db";
const AHREFS_KEY = "mt7l/CRPBs6rvpF/5Uzu7w";
const CLOUDFLARE_BEACON_TOKEN = "fb823f6bfd2b4319afade1a48e6e9030";
const GHL_AFFILIATE_ID = "AOzAVlvHlRJyZtr5j7Xo";

type ConsentSignal = "granted" | "denied";

interface Tool {
  readonly group: ConsentGroup;
  /** Starts the tool with the visitor's groups. */
  readonly start: (allowed: ConsentGroups) => void;
  /** Tells the running tool that the visitor's groups changed. */
  readonly update?: (allowed: ConsentGroups) => void;
}

/** A vendor's stub that queues calls until its script replaces it. */
type QueuedFunction = ((...args: unknown[]) => void) & { q?: IArguments[] };

type MetaPixel = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: IArguments[];
  push?: MetaPixel;
  loaded: boolean;
  version: string;
};

function signal(allowed: boolean): ConsentSignal {
  return allowed ? "granted" : "denied";
}

function injectScript(
  src: string,
  options: { attributes?: Record<string, string>; onLoad?: () => void } = {},
): void {
  const script = document.createElement("script");
  script.src = src;
  script.async = true;
  for (const [name, value] of Object.entries(options.attributes ?? {})) {
    script.setAttribute(name, value);
  }
  if (options.onLoad) script.addEventListener("load", options.onLoad);
  document.head.appendChild(script);
}

function googleConsent(allowed: ConsentGroups): Record<string, ConsentSignal> {
  const ads = signal(allowed.marketing);
  return {
    analytics_storage: signal(allowed.analytics),
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
  };
}

const sentryReplay: Tool = {
  group: "analytics",
  start: startErrorReplay,
};

const googleAnalytics: Tool = {
  group: "analytics",
  start(allowed) {
    const dataLayer = (window.dataLayer ??= []);
    window.gtag = function () {
      // gtag.js reads a queued command only as an `arguments` object.
      dataLayer.push(arguments);
    };
    window.gtag("consent", "default", googleConsent(allowed));
    window.gtag("js", new Date());
    window.gtag("config", GOOGLE_ANALYTICS_ID);
    injectScript(
      `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`,
    );
  },
  update(allowed) {
    window.gtag?.("consent", "update", googleConsent(allowed));
  },
};

function clarityConsent(allowed: ConsentGroups): Record<string, ConsentSignal> {
  return {
    ad_Storage: signal(allowed.marketing),
    analytics_Storage: signal(allowed.analytics),
  };
}

const clarity: Tool = {
  group: "analytics",
  start(allowed) {
    // Clarity's stub: it queues calls in `q` until its script loads.
    const queued: QueuedFunction = function () {
      (queued.q ??= []).push(arguments);
    };
    window.clarity = queued;
    queued("consentv2", clarityConsent(allowed));
    injectScript(`https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`);
  },
  update(allowed) {
    window.clarity?.("consentv2", clarityConsent(allowed));
    // Clarity erases its cookies when consent is withdrawn this way.
    if (!allowed.analytics) window.clarity?.("consent", false);
  },
};

const ahrefs: Tool = {
  group: "analytics",
  start() {
    injectScript("https://analytics.ahrefs.com/analytics.js", {
      attributes: { "data-key": AHREFS_KEY },
    });
  },
};

const cloudflareAnalytics: Tool = {
  group: "analytics",
  start() {
    injectScript("https://static.cloudflareinsights.com/beacon.min.js", {
      attributes: {
        // As in Cloudflare's own snippet.
        type: "module",
        "data-cf-beacon": JSON.stringify({ token: CLOUDFLARE_BEACON_TOKEN }),
      },
    });
  },
};

const siteBehaviour: Tool = {
  group: "analytics",
  start() {
    window.sitebehaviourTrackingSecret = SITEBEHAVIOUR_SECRET;
    injectScript(
      `https://sitebehaviour-cdn.fra1.cdn.digitaloceanspaces.com/index.min.js?sitebehaviour-secret=${SITEBEHAVIOUR_SECRET}`,
    );
  },
};

const metaPixel: Tool = {
  group: "marketing",
  start() {
    // Meta's stub: it queues calls until fbevents.js sets `callMethod`.
    const pixel: MetaPixel = Object.assign(
      function (): void {
        if (pixel.callMethod) {
          Reflect.apply(pixel.callMethod, pixel, arguments);
        } else {
          pixel.queue.push(arguments);
        }
      },
      { queue: new Array<IArguments>(), loaded: true, version: "2.0" },
    );
    pixel.push = pixel;
    window.fbq = pixel;
    window._fbq ??= pixel;
    injectScript("https://connect.facebook.net/en_US/fbevents.js");
    for (const id of META_PIXEL_IDS) {
      // Automatic events would send the text of clicked buttons, which can
      // hold decrypted Life Design names, to Meta. Only explicit tracks run.
      pixel("set", "autoConfig", false, id);
      pixel("init", id);
      pixel("track", "PageView");
    }
  },
  update(allowed) {
    if (!allowed.marketing) window.fbq?.("consent", "revoke");
  },
};

const ghlAffiliate: Tool = {
  group: "marketing",
  start() {
    injectScript("https://link.msgsndr.com/js/am.js", {
      onLoad: () => {
        try {
          window.affiliateManager?.init(
            GHL_AFFILIATE_ID,
            "https://backend.leadconnectorhq.com",
            ".dreamcoachmatch.com",
          );
        } catch {
          // As in GoHighLevel's snippet: a failure inside the partner script
          // must not break the page or reach Sentry as the app's error.
        }
      },
    });
  },
};

const TOOLS: readonly Tool[] = [
  sentryReplay,
  googleAnalytics,
  clarity,
  ahrefs,
  cloudflareAnalytics,
  siteBehaviour,
  metaPixel,
  ghlAffiliate,
];

const started = new Set<Tool>();

/**
 * Starts every allowed tool that has not started on this page and gives the
 * running ones the visitor's groups, so they can stop storing data.
 */
export function syncConsentedTools(allowed: ConsentGroups): void {
  for (const tool of TOOLS) {
    if (started.has(tool)) {
      tool.update?.(allowed);
    } else if (allowed[tool.group]) {
      started.add(tool);
      tool.start(allowed);
    }
  }
}

/**
 * Stops the tools of the groups the visitor turned off. Google, Clarity and
 * Meta hear the withdrawal first; the other tools have no off switch, so the
 * page reloads without them.
 */
export function withdrawConsentedTools(allowed: ConsentGroups): void {
  syncConsentedTools(allowed);
  window.location.reload();
}

/**
 * The cookies each group's tools set on DCM's domain, from vendor
 * documentation and the scripts themselves: Google Analytics and Clarity; then
 * the Meta Pixel, the Google Ads cookie the Google tag writes under
 * `ad_storage` when a Google Ads account is linked to it, and the GoHighLevel
 * affiliate script (`link.msgsndr.com/js/am.js`), which keeps a visitor id,
 * fingerprint and sign-up flag. The summit agenda test keeps its variant in
 * the analytics group's cookie too. SiteBehaviour keeps ids in web storage
 * (`SITEBEHAVIOUR_STORAGE`); Ahrefs and Cloudflare Web Analytics store nothing.
 */
const VENDOR_COOKIES: Readonly<Record<ConsentGroup, RegExp>> = {
  analytics: /^(_ga|_ga_\w+|_gid|_gat\w*|_clck|_clsk|summit_agenda_variant)$/,
  marketing: /^(_fbp|_fbc|_gcl_au|am_id|am_fingerprint|am_signed_up)$/,
};

/** The ids SiteBehaviour keeps in web storage instead of cookies. */
const SITEBEHAVIOUR_STORAGE = [
  { storage: "sessionStorage", key: "siteBehaviour_session_id_v1" },
  { storage: "localStorage", key: "user_id" },
] as const;

/** The host itself, then each parent domain a vendor may have used. */
function cookieDomainAttributes(hostname: string): string[] {
  const labels = hostname.split(".");
  const attributes = [""];
  for (let start = 0; start < labels.length - 1; start++) {
    attributes.push(`; domain=.${labels.slice(start).join(".")}`);
  }
  return attributes;
}

function removeSiteBehaviourIds(): void {
  for (const { storage, key } of SITEBEHAVIOUR_STORAGE) {
    try {
      window[storage].removeItem(key);
    } catch {
      // Blocked storage holds nothing of ours to remove.
    }
  }
}

/** Deletes the vendor cookies and stored ids of every group that is not allowed. */
export function deleteVendorCookies(allowed: ConsentGroups): void {
  if (!allowed.analytics) removeSiteBehaviourIds();
  const domains = cookieDomainAttributes(window.location.hostname);
  for (const pair of document.cookie.split(";")) {
    const name = pair.split("=")[0]?.trim();
    if (!name) continue;
    const withdrawn = (["analytics", "marketing"] as const).some(
      (group) => !allowed[group] && VENDOR_COOKIES[group].test(name),
    );
    if (!withdrawn) continue;
    for (const domain of domains) {
      document.cookie = `${name}=; path=/; max-age=0${domain}`;
    }
  }
}
